import { useCallback, useEffect, useRef } from 'react';

import { getUserConsentCreatedStorage } from '@jsmdg/browser-storage';
import { logToSentry, useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { CustomUIEventEnum } from '@jsmdg/ui-event';
import { getUserConsent } from '../helper/getUserConsent';
import { type FooterContext } from '../types';

const USERCENTRICS_INITIALIZED_EVENT = 'UC_UI_INITIALIZED';
const USERCENTRICS_CONSENT_CHANGED_EVENT = 'onConsentStatusChange';

export const useUserConsent = () => {
    const { addUserConsent, updateUserConsent } = useFragmentContext<FooterContext>();

    const abortController = useRef<AbortController>(new AbortController());

    const sendUserConsent = useCallback(
        async (email: string) => {
            const userConsentCreatedStorage = getUserConsentCreatedStorage();
            const isUserConsentCreated = userConsentCreatedStorage.get() === 'true';

            if (isUserConsentCreated) {
                return;
            }

            const userConsent = getUserConsent();

            if (!userConsent) {
                return;
            }

            try {
                await addUserConsent(email, userConsent);

                userConsentCreatedStorage.set('true');
            } catch (error) {
                logToSentry(error instanceof Error ? error : String(error));
            }
        },
        [addUserConsent],
    );

    const sendChangedConsent = useCallback(async () => {
        const userConsentCreatedStorage = getUserConsentCreatedStorage();
        const shouldSend = userConsentCreatedStorage.get() === 'true';

        if (!shouldSend) {
            return;
        }

        const userConsent = getUserConsent();

        if (!userConsent) {
            return;
        }

        try {
            await updateUserConsent(userConsent);
        } catch (error) {
            logToSentry(error instanceof Error ? error : String(error));
        }
    }, [updateUserConsent]);

    const onUCInitialized = useCallback(() => {
        (async () => {
            const customerDataFetchedEvents = window.Evelin.eventHistory.getEventsByType(
                CustomUIEventEnum.CustomerDataFetched,
                { olderThan: Date.now() },
            );

            for (const event of customerDataFetchedEvents) {
                await sendUserConsent(event.detail.email);
            }

            document.addEventListener(
                CustomUIEventEnum.CustomerDataFetched,
                event => {
                    sendUserConsent(event.detail.email);
                },
                { signal: abortController.current.signal },
            );
        })();
    }, []);

    useEffect(() => {
        window.addEventListener(USERCENTRICS_INITIALIZED_EVENT, onUCInitialized, {
            signal: abortController.current.signal,
        });

        window.addEventListener(
            USERCENTRICS_CONSENT_CHANGED_EVENT,
            () => {
                sendChangedConsent();
            },
            {
                signal: abortController.current.signal,
            },
        );

        return () => {
            abortController.current.abort();
        };
    }, []);
};
